<template>
  <div class="renew-modal-success__container">
    <div class="renew-modal-success__header">
      <ConfettiExplosion
        class="renew-modal-success__confetti"
        :repeatCount="1"
      />

      <h4 class="renew-modal-success__congrat-title">
        {{ congratTitle }}
      </h4>
      <h2 class="renew-modal-success__title">
        {{ title }}
      </h2>
    </div>
    <div class="renew-modal-success__info-wrapper">
      <div v-if="!isRenewal" class="renew-modal-success__info">
        You're all set to take the next step.
      </div>
      <div class="renew-modal-success__info renew-modal-success__info--text">
        {{ infoText }}
      </div>
    </div>
    <div class="renew-modal-success__cta-wrapper">
      <button
        class="btn btn--primary renew-modal-success__btn"
        :disabled="isLoading"
        @click="clickHandler()"
      >
        <span v-if="!isLoading">{{ ctaBtnLabel }}</span>
        <ActionLoader
          v-else
          class="renew-modal-success__loading"
          name="ball-clip-rotate"
        />
      </button>
      <a
        v-if="isRenewal"
        class="btn btn--secondary btn--default-icon-color"
        :href="linkedInUrl"
        target="_blank"
        :disabled="isLinkedinDisabled"
      >
        <LinkedinIcon /> Share on LinkedIn
      </a>

      <div class="renew-modal-success__cta-info" v-html="footerInfo"></div>
    </div>
  </div>
</template>

<script>
import CONSTANTS from '@/constants'
import { mapActions, mapGetters } from 'vuex'
import { LinkedinIcon } from 'vue-feather-icons'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import ConfettiExplosion from '@/components/shared/ConfettiExplosion.vue'
import { certificationCommons } from '@/helpers/mixins'
import { isEmpty } from 'lodash'

const { SHARE_URL, ORGANIZATION_ID } = CONSTANTS.LINKEDIN
const { IDS_MERCHANT_IDS } = CONSTANTS.CERTIFICATIONS

export default {
  name: 'CPERenewModalSuccess',
  components: { LinkedinIcon, ActionLoader, ConfettiExplosion },
  mixins: [certificationCommons],
  data() {
    return {
      isLinkedinDisabled: false,
      isLoading: false,
      showConfetti: false,
      vocuhersRequestAttempts: 0,
      learningPathUrlParams: {},
      ctaBtnLabel: '',
    }
  },
  computed: {
    ...mapGetters({
      cpeGlobalCredits: 'cpeProgram/cpeGlobalCredits',
      productCertificationVouchers:
        'subscriptions/getProductCertificationVouchers',
      currentOption: 'cpeProgram/cpeRenewModalOption',
      renewalModalStatus: 'cpeProgram/cpeRenewModalStatus',
      userVouchers: 'certifications/usersVouchers',
    }),
    certification() {
      return this.renewalModalStatus.certification
    },
    isRenewal() {
      return this.renewalModalStatus.isRenewal
    },
    congratTitle() {
      return `${this.isRenewal ? 'RENEWAL' : 'PURCHASE'} COMPLETE`
    },
    title() {
      return `Your ${
        this.isRenewal ? `renewed ${this.certification.short_name}` : 'voucher'
      } is ready!`
    },
    infoText() {
      return this.isRenewal
        ? `Congratulations on renewing your ${this.certification.name} certification! Your certification is now valid until ${this.renewalModalStatus.renewalDate}.`
        : "Start with training if you need to prepare, or take the exam now if you're ready. Remember, you have two attempts, so start when you feel confident."
    },
    btnText() {
      return this.isRenewal ? 'See certification' : 'Go to training'
    },
    footerInfo() {
      return this.isRenewal
        ? `CPE credits update: You have <span>${this.cpeGlobalCredits} CPE</span> credits remaining.`
        : 'Best of luck on your journey!'
    },
    certificateLinkedinURL() {
      return `${window.location.origin}/certificate/${this.certification?.attempt_id}`
    },
    certificateURL() {
      return `/certificate/${this.certification?.attempt_id}`
    },
    linkedInUrl() {
      if (!this.isRenewal) return ''
      const certId = this.certification.attempt_id?.split('-')[0]
      if (this.certification.awarded) {
        const finishedAt = new Date(this.certification.awarded)
        const expirationDate = this.certification.cert_awarded_expiration_date
          ? new Date(this.certification.cert_awarded_expiration_date)
          : null

        const expirationParams = expirationDate
          ? `&expirationYear=${expirationDate.getFullYear()}&expirationMonth=${expirationDate.getMonth() +
              1}`
          : ''

        return `${SHARE_URL}?startTask=CERTIFICATION_NAME&name=${
          this.certification.name
        }&organizationId=${ORGANIZATION_ID}&issueYear=${finishedAt.getFullYear()}&issueMonth=${finishedAt.getMonth() +
          1}&certId=${certId}&certUrl=${
          this.certificateLinkedinURL
        }${expirationParams}`
      }
      return `${SHARE_URL}?startTask=CERTIFICATION_NAME&name=${this.certification.name}&organizationId=${ORGANIZATION_ID}&certId=${certId}&certUrl=${this.certificateURL}`
    },
  },
  async created() {
    if (!this.isRenewal) {
      this.setLearningPathUrlParams()
      if (isEmpty(this.learningPathUrlParams)) this.handleUserVouchersRequest()
      return (this.showConfetti = true)
    }
  },
  mounted() {
    this.ctaBtnLabel = this.btnText
  },
  methods: {
    ...mapActions({
      setRenewModalStatus: 'cpeProgram/setRenewModalStatus',
      getUsersVouchers: 'certifications/getUsersVouchers',
    }),
    setModalStatus(values) {
      const params = {
        isCurrentCert: false,
      }
      this.setRenewModalStatus({ ...params, ...values })
    },

    async handleUserVouchersRequest() {
      const maxAttempts = 3
      const delay = 1000

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          this.isLoading = true
          await this.getUsersVouchers()
          this.setLearningPathUrlParams()

          if (!isEmpty(this.learningPathUrlParams)) {
            this.isLoading = false
            break
          }
        } catch (error) {
          this.isLoading = false
          this.$store.dispatch(
            'app/setNotification',
            `Your purchase is processing. Once complete, we'll email your receipt, and your voucher will be available in your profile.`,
            { root: true }
          )
        }

        if (attempt < maxAttempts) {
          this.isLoading = true
          await new Promise(resolve => setTimeout(resolve, delay))
        } else {
          this.isLoading = false
          this.$store.dispatch(
            'app/setNotification',
            `Your purchase is processing. Once complete, we'll email your receipt, and your voucher will be available in your profile.`,
            { root: true }
          )
        }
      }
    },

    setLearningPathUrlParams() {
      const currentMerchantId = this.renewalModalStatus?.currentSelection
        .merchant_id
      const purchasedCertId = Object.keys(IDS_MERCHANT_IDS).find(
        key => IDS_MERCHANT_IDS[key] === currentMerchantId
      )

      const purchasedCert = this.userVouchers.find(
        cert => cert.id === purchasedCertId
      )
      const currentLearningPath = purchasedCert?.learning_paths[0]

      if (purchasedCert && currentLearningPath) {
        this.learningPathUrlParams = {
          ...currentLearningPath,
          area: purchasedCert?.learning_area?.name?.replace(/\s/g, ''),
        }
      } else {
        this.ctaBtnLabel = 'Close'
      }
    },
    clickHandler() {
      this.isLoading = true
      if (this.isRenewal) return this.$router.push(this.certificateURL)
      if (!isEmpty(this.learningPathUrlParams)) {
        return this.getTrainingClick(this.learningPathUrlParams)
      } else {
        this.$parent.$emit('close-modal')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.renew-modal-success {
  &__container {
    display: flex;
    flex-direction: column;
  }
  &__header {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  &__congrat-title {
    font-size: rem(18);
    line-height: rem(24);
    color: var(--positive-light);
    font-weight: 500;
    margin: 0 0 10px 0;
  }
  &__title {
    font-size: rem(32);
    line-height: rem(40);
    margin: 0;
    text-align: center;
    @include mobile {
      font-size: rem(40);
      line-height: rem(52);
      margin: 0;
    }
  }
  &__info-wrapper {
    display: flex;
    flex-direction: column;
    gap: 15px;
    margin: 20px 0 40px;
  }
  &__info {
    color: var(--white);
    font-size: rem(18);
    line-height: rem(24);
    text-align: center;
    &--text {
      color: var(--light-blue-gray);
    }
  }
  &__cta-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    margin-bottom: 50px;
  }
  &__cta-info {
    font-size: rem(16);
    line-height: rem(24);
    text-align: center;
    font-weight: 700;
    margin-top: 20px;
    ::v-deep span {
      color: var(--ice-cold);
    }
  }
  &__btn {
    min-width: 130px;
  }
  &__confetti {
    position: absolute;
    top: 25px;
    width: 150px;
    height: 150px;
  }
  &__loading-container {
    height: 460px;
    margin-top: -65px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  &__loading {
    margin-top: 5px;
  }
}
</style>
